/**
 * EN Accordion Wrapper
 * Collapses/expands sections between en-accordion-start and en-accordion-end markers
 * Sections remain in their original position to avoid conflicts with existing scripts
 */

class ENAccordionWrapper {
  constructor() {
    this.accordions = [];
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initAfterDOM());
    } else {
      this.initAfterDOM();
    }
  }

  initAfterDOM() {
    const startMarkers = document.querySelectorAll('.en-accordion-start');
    const endMarkers = document.querySelectorAll('.en-accordion-end');

    // Always validate markers (even in theme editor) for visual error indicators
    this.validateMarkers(startMarkers, endMarkers);

    // Don't initialize accordions in Shopify theme editor/admin
    if (this.isShopifyAdmin()) {
      console.log('EN Accordion: Disabled in Shopify theme editor');
      return;
    }

    // Setup accordions on live site
    this.setupAccordions();
  }

  isShopifyAdmin() {
    // Check if in Shopify theme editor (design mode)
    if (window.Shopify && window.Shopify.designMode) {
      return true;
    }

    // Fallback: check for theme editor specific classes on body
    if (document.body.classList.contains('theme-editor')) {
      return true;
    }

    // Additional check: look for Shopify editor iframe
    if (window.frameElement && window.frameElement.id === 'preview-frame') {
      return true;
    }

    return false;
  }

  setupAccordions() {
    const startMarkers = document.querySelectorAll('.en-accordion-start');

    // Only populate valid accordions (validation already done in initAfterDOM)
    startMarkers.forEach((startMarker) => {
      this.populateAccordion(startMarker);
    });
  }

  validateMarkers(startMarkers, endMarkers) {
    // Check each start marker has a matching end
    startMarkers.forEach((startMarker) => {
      const endMarker = this.findEndMarker(startMarker);

      if (!endMarker) {
        // Mark as orphaned
        startMarker.dataset.orphaned = 'true';
        startMarker.dataset.errorType = 'missing-end';

        const accordionId = startMarker.dataset.accordionId;
        const accordion = accordionId ? document.getElementById(accordionId) : null;
        if (accordion) {
          accordion.dataset.orphaned = 'true';
          accordion.style.display = 'none'; // Hide broken accordion
        }

        console.error('EN Accordion: Missing end marker for accordion', accordionId);
      } else {
        startMarker.dataset.orphaned = 'false';
      }
    });

    // Check each end marker has a matching start
    endMarkers.forEach((endMarker) => {
      const hasMatchingStart = Array.from(startMarkers).some((startMarker) => {
        const foundEnd = this.findEndMarker(startMarker);
        return foundEnd === endMarker;
      });

      if (!hasMatchingStart) {
        endMarker.dataset.orphaned = 'true';
        endMarker.dataset.errorType = 'missing-start';
        console.error('EN Accordion: Orphaned end marker found', endMarker);
      } else {
        endMarker.dataset.orphaned = 'false';
      }
    });
  }

  populateAccordion(startMarker) {
    // Skip orphaned markers (graceful degradation)
    if (startMarker.dataset.orphaned === 'true') {
      return;
    }

    // Get the accordion ID from the marker
    const accordionId = startMarker.dataset.accordionId;
    if (!accordionId) {
      console.warn('EN Accordion: No accordion ID found on start marker', startMarker);
      return;
    }

    // Find the pre-rendered accordion element
    const accordionWrapper = document.getElementById(accordionId);
    if (!accordionWrapper) {
      console.warn('EN Accordion: No accordion element found with ID', accordionId);
      return;
    }

    // Skip if accordion is marked as orphaned
    if (accordionWrapper.dataset.orphaned === 'true') {
      return;
    }

    // Check if already initialized (prevent duplicate initialization)
    if (accordionWrapper.dataset.initialized === 'true') {
      return;
    }

    // Find the end marker
    const endMarker = this.findEndMarker(startMarker);
    if (!endMarker) {
      console.warn('EN Accordion: No end marker found for start marker', startMarker);
      return;
    }

    // Collect all sections between start and end markers
    const sections = this.getSectionsBetween(startMarker, endMarker);

    if (sections.length === 0) {
      console.warn('EN Accordion: No sections found between markers');
      return;
    }

    // Mark sections with accordion control class (don't move them)
    sections.forEach(section => {
      section.classList.add('en-accordion-controlled');
      section.dataset.accordionId = accordionId;
    });

    // Set up click handler
    this.setupClickHandler(accordionWrapper, sections);

    // Set initial visibility based on accordion state
    this.updateSectionVisibility(accordionWrapper, sections);

    // Set initial state class on end section
    const isOpen = accordionWrapper.getAttribute('data-state') === 'open';
    const endSection = endMarker.closest('.shopify-section');
    if (endSection) {
      if (isOpen) {
        endSection.classList.add('en-accordion-is-open');
      } else {
        endSection.classList.remove('en-accordion-is-open');
      }
    }

    // Mark as initialized
    accordionWrapper.dataset.initialized = 'true';

    // Store reference
    this.accordions.push({
      wrapper: accordionWrapper,
      startMarker,
      endMarker,
      sections
    });
  }

  findEndMarker(startMarker) {
    // Start from the parent section's next sibling
    const startSection = startMarker.closest('.shopify-section');
    if (!startSection) {
      console.warn('EN Accordion: Start marker not within a shopify-section', startMarker);
      return null;
    }

    let currentElement = startSection.nextElementSibling;

    while (currentElement) {
      // Check if this section contains the end marker
      const endMarker = currentElement.querySelector('.en-accordion-end');
      if (endMarker) {
        return endMarker;
      }

      currentElement = currentElement.nextElementSibling;
    }

    console.warn('EN Accordion: End marker not found after start section', startSection);
    return null;
  }

  getSectionsBetween(startMarker, endMarker) {
    const sections = [];

    // Start from the parent section's next sibling
    const startSection = startMarker.closest('.shopify-section');
    if (!startSection) {
      console.warn('EN Accordion: Start marker not within a shopify-section', startMarker);
      return sections;
    }

    const endSection = endMarker.closest('.shopify-section');
    if (!endSection) {
      console.warn('EN Accordion: End marker not within a shopify-section', endMarker);
      return sections;
    }

    let currentElement = startSection.nextElementSibling;

    while (currentElement && currentElement !== endSection) {
      sections.push(currentElement);
      currentElement = currentElement.nextElementSibling;
    }

    return sections;
  }

  setupClickHandler(accordionWrapper, sections) {
    const header = accordionWrapper.querySelector('.en-accordion__header');
    const content = accordionWrapper.querySelector('.en-accordion__content');

    if (!header) {
      console.warn('EN Accordion: No header found in accordion', accordionWrapper);
      return;
    }

    // Check if event listeners already attached (extra safety check)
    if (header.dataset.listenersAttached === 'true') {
      return;
    }

    // Handle click events
    header.addEventListener('click', () => {
      this.toggleAccordion(accordionWrapper, sections);
    });

    // Handle keyboard navigation
    header.addEventListener('keydown', (e) => {
      switch(e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          this.focusNextAccordion(accordionWrapper);
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault();
          this.focusPreviousAccordion(accordionWrapper);
          break;
        case 'Home':
          e.preventDefault();
          this.focusFirstAccordion();
          break;
        case 'End':
          e.preventDefault();
          this.focusLastAccordion();
          break;
      }
    });

    // Mark listeners as attached
    header.dataset.listenersAttached = 'true';
  }

  toggleAccordion(accordionWrapper, sections) {
    const header = accordionWrapper.querySelector('.en-accordion__header');
    const isOpen = accordionWrapper.getAttribute('data-state') === 'open';
    const newState = isOpen ? 'closed' : 'open';

    accordionWrapper.setAttribute('data-state', newState);
    header.setAttribute('aria-expanded', !isOpen);

    // Update end marker section class based on state
    const accordionData = this.accordions.find(acc => acc.wrapper === accordionWrapper);
    if (accordionData && accordionData.endMarker) {
      const endSection = accordionData.endMarker.closest('.shopify-section');
      if (endSection) {
        if (newState === 'open') {
          endSection.classList.add('en-accordion-is-open');
        } else {
          endSection.classList.remove('en-accordion-is-open');
        }
      }
    }

    this.updateSectionVisibility(accordionWrapper, sections);
  }

  focusNextAccordion(currentAccordion) {
    const currentIndex = this.accordions.findIndex(acc => acc.wrapper === currentAccordion);
    if (currentIndex < this.accordions.length - 1) {
      const nextHeader = this.accordions[currentIndex + 1].wrapper.querySelector('.en-accordion__header');
      nextHeader.focus();
    }
  }

  focusPreviousAccordion(currentAccordion) {
    const currentIndex = this.accordions.findIndex(acc => acc.wrapper === currentAccordion);
    if (currentIndex > 0) {
      const prevHeader = this.accordions[currentIndex - 1].wrapper.querySelector('.en-accordion__header');
      prevHeader.focus();
    }
  }

  focusFirstAccordion() {
    if (this.accordions.length > 0) {
      const firstHeader = this.accordions[0].wrapper.querySelector('.en-accordion__header');
      firstHeader.focus();
    }
  }

  focusLastAccordion() {
    if (this.accordions.length > 0) {
      const lastHeader = this.accordions[this.accordions.length - 1].wrapper.querySelector('.en-accordion__header');
      lastHeader.focus();
    }
  }

  updateSectionVisibility(accordionWrapper, sections) {
    const isOpen = accordionWrapper.getAttribute('data-state') === 'open';

    sections.forEach(section => {
      if (isOpen) {
        // Opening: First set display, then animate
        section.style.display = '';
        section.style.overflow = 'hidden';
        section.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
        section.style.opacity = '0';
        section.style.maxHeight = '0';

        // Trigger reflow to ensure transition works
        section.offsetHeight;

        // Get the actual height
        section.style.maxHeight = section.scrollHeight + 'px';
        section.style.opacity = '1';

        // Clean up after transition
        setTimeout(() => {
          section.style.maxHeight = '';
          section.style.overflow = '';
          section.classList.remove('en-accordion-hidden');
        }, 300);
      } else {
        // Closing: Animate then hide
        section.classList.add('en-accordion-hidden');
        section.style.overflow = 'hidden';
        section.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
        section.style.maxHeight = section.scrollHeight + 'px';

        // Trigger reflow
        section.offsetHeight;

        section.style.opacity = '0';
        section.style.maxHeight = '0';

        // Hide after animation completes
        setTimeout(() => {
          section.style.display = 'none';
        }, 300);
      }
    });
  }
}

// Initialize when script loads
new ENAccordionWrapper();
